import { useTranslation } from "react-i18next"

import type { UserDocumentParticipantRole } from "@/api/models"
import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { Share2, UserPlus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import {
  useAddDocumentParticipant,
  useDocumentParticipants,
  useRemoveDocumentParticipant,
  useTransferDocumentOwnership,
} from "@/hooks/use-documents"
import { SettingsService } from "@/services/settings-service"

import { ParticipantList } from "../../projects/components/participant-list"

type DocumentShareDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentId: string
  currentUserRole?: UserDocumentParticipantRole
  ownerUserId?: string
}

export function DocumentShareDialog({
  open,
  onOpenChange,
  documentId,
  currentUserRole,
  ownerUserId,
}: DocumentShareDialogProps) {
  const { t } = useTranslation()
  const currentUserOid = SettingsService.getInstance().claims.oid

  const { data: participantsData, isLoading } = useDocumentParticipants(documentId)
  const addParticipant = useAddDocumentParticipant()
  const removeParticipant = useRemoveDocumentParticipant()
  const transferOwnership = useTransferDocumentOwnership()

  const resolvedCurrentUserRole = participantsData?.currentUserRole ?? currentUserRole
  const resolvedOwnerUserId = participantsData?.ownerUserId ?? ownerUserId
  const canManage = resolvedCurrentUserRole === "owner"

  const existingOids = (
    participantsData?.participants.map((participant) => participant.oid) ?? []
  ).filter(Boolean) as string[]

  const handleAddParticipant = async (result: DirectorySearchResult) => {
    if (existingOids.includes(result.oid)) {
      return
    }

    await addParticipant.mutateAsync({
      documentId,
      participant: {
        oid: result.oid,
        displayName: result.displayName,
        type: result.type,
        role: "reader",
      },
    })
  }

  const handleRemove = async (participantOid: string) => {
    await removeParticipant.mutateAsync({
      documentId,
      participantOid,
    })
  }

  const handleTransferOwnership = async (newOwnerOid: string) => {
    await transferOwnership.mutateAsync({
      documentId,
      newOwnerOid,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="size-5" />
            {t("sharing.dialog_title_document", { defaultValue: "Share Document" })}
          </DialogTitle>
          <DialogDescription>
            {t("sharing.dialog_description_document", {
              defaultValue: "Add users or groups to collaborate on this document.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {canManage && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <UserPlus className="text-muted-foreground size-4" />
                <Label className="text-sm font-medium">
                  {t("sharing.add_participant", { defaultValue: "Add People" })}
                </Label>
              </div>

              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <DirectorySearch
                    disabled={addParticipant.isPending}
                    selectedOids={existingOids}
                    onSelect={handleAddParticipant}
                  />
                </div>
              </div>

              <Separator />
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm font-medium">
              {t("sharing.participants", { defaultValue: "People with Access" })}
            </Label>
            <ParticipantList
              allowedRoles={["reader"]}
              canManage={canManage}
              currentUserOid={currentUserOid}
              entityType="document"
              isCurrentUserOwner={resolvedCurrentUserRole === "owner"}
              isLoading={isLoading}
              ownerUserId={resolvedOwnerUserId}
              participants={participantsData?.participants ?? []}
              emptyParticipantsText={t("sharing.no_participants_document", {
                defaultValue: "No participants yet. Add users or groups to share this document.",
              })}
              onRemove={handleRemove}
              onRoleChange={undefined}
              onTransferOwnership={handleTransferOwnership}
            />
          </div>

          <div className="bg-muted/50 text-muted-foreground rounded-md px-3 py-2 text-xs">
            <p className="mb-1 font-medium">
              {t("sharing.role_descriptions_title", { defaultValue: "Permission levels:" })}
            </p>
            <ul className="space-y-0.5">
              <li>
                <strong>{t("sharing.role_reader", { defaultValue: "Reader" })}:</strong>{" "}
                {t("sharing.role_reader_desc_document", {
                  defaultValue: "Can view the document",
                })}
              </li>
              <li>
                <strong>{t("sharing.role_owner", { defaultValue: "Owner" })}:</strong>{" "}
                {t("sharing.role_owner_desc_document", {
                  defaultValue: "Can edit, share, transfer ownership, and delete the document",
                })}
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close", { defaultValue: "Close" })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
